import React, {useState} from "react";
import {Link as RouterLink} from "react-router-dom";
import {
    Alert,
    Avatar,
    Box,
    Button,
    Container,
    MenuItem,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import {CheckCircleOutline} from "@mui/icons-material";
import {useUserAuth} from "../../context/UserAuthContext";
import {addProjectWithImage, COLLEGES} from "../../services/associationService";
import PortalTopbar from "./PortalTopbar";

const PRIMARY = "#3E4F88";


// Na app: o picker "Tipo" alimenta o campo Time; o picker "Horário" alimenta o campo Type
const MODALITIES = ["Remoto", "Presencial", "Híbrido"];
const SCHEDULES = ["Full-Time", "Part-Time", "Livre"];

/**
 * Adicionar projeto — espelha o ProjectAddPage da app:
 * mesmos campos, mesmas opções, mesma imagem Projects/{id}.jpg,
 * criado com Enabled=false (um admin aprova depois, igual à app).
 */
const ProjectAddPage = () => {

    const {profile} = useUserAuth();
    const associationName = profile && profile.Name ? profile.Name : "";

    const [title, setTitle] = useState("");
    const [location, setLocation] = useState("");
    const [college, setCollege] = useState("");
    const [modality, setModality] = useState(MODALITIES[0]); // -> campo Time
    const [schedule, setSchedule] = useState(SCHEDULES[0]);     // -> campo Type
    const [description, setDescription] = useState("");
    const [image, setImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);

    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [submittedId, setSubmittedId] = useState(null);
    // true = o projeto foi criado mas o upload da imagem falhou (usámos a imagem por omissão)
    const [photoFallback, setPhotoFallback] = useState(false);

    const onImageChange = (e) => {
        const file = e.target.files && e.target.files[0];
        setImage(file || null);
        if (file) {
            setImagePreview(URL.createObjectURL(file));
        } else {
            setImagePreview(null);
        }
    };

    const submit = async (e) => {
        e.preventDefault();
        setError("");

        if (!title.trim()) {
            setError("Indica o título do projeto.");
            return;
        }
        if (!location.trim()) {
            setError("Indica a localização do projeto.");
            return;
        }
        if (!college) {
            setError("Escolhe o curso (colégio).");
            return;
        }
        if (!description.trim()) {
            setError("Escreve a descrição do projeto.");
            return;
        }
        if (!associationName) {
            setError("Não foi possível identificar a vossa associação. Faz logout e login novamente.");
            return;
        }

        setLoading(true);
        try {
            const project = {
                Title: title.trim(),
                Association: associationName,
                Location: location.trim(),
                College: college,
                Time: modality,   // na app: "Tipo" (Remoto/Presencial/Híbrido)
                Type: schedule,   // na app: "Horário" (Full-Time/Part-Time/Livre)
                Disc: description.trim(),
                Logo: "",         // substituído pela URL da imagem (ou fallback) no serviço
            };
            const result = await addProjectWithImage(project, image);
            setSubmittedId(result.id);
            setPhotoFallback(result.photoFallback);
        } catch (err) {
            console.error("Erro ao criar projeto:", err);
            setError("Não foi possível criar o projeto. Tenta novamente.");
        } finally {
            setLoading(false);
        }
    };

    // ---------- vista de sucesso ----------
    if (submittedId) {
        return (
            <Box minHeight="100vh" sx={{bgcolor: "#f4f5fa"}}>
                <PortalTopbar/>
                <Container maxWidth="sm" sx={{py: 8}}>
                    <Stack spacing={3} alignItems="center" sx={{bgcolor: "background.paper", border: 1, borderColor: "divider", borderRadius: 2, p: 5}}>
                        <CheckCircleOutline sx={{fontSize: 56, color: "#2e7d32"}}/>
                        <Alert severity="success" sx={{alignItems: "flex-start", textAlign: "left"}}>
                            Projeto submetido. Deverá aguardar que um administrador da plataforma aceite o seu projeto.
                        </Alert>
                        {photoFallback && (
                            <Alert severity="warning" sx={{alignItems: "flex-start", textAlign: "left"}}>
                                Não foi possível carregar a imagem do projeto de momento,
                                pelo que o projeto ficou com a imagem por omissão.
                            </Alert>
                        )}
                        <Typography variant="body2" color="text.secondary" sx={{textAlign: "center"}}>
                            Quando o projeto for aprovado, aparecerá na vossa lista de projetos.
                        </Typography>
                        <Stack direction="row" spacing={2}>
                            <Button component={RouterLink} to="/app/associacao" variant="contained" sx={{bgcolor: PRIMARY, fontWeight: 600}}>
                                Voltar à lista
                            </Button>
                        </Stack>
                    </Stack>
                </Container>
            </Box>
        );
    }

    // ---------- formulário ----------
    return (
        <Box minHeight="100vh" sx={{bgcolor: "#f4f5fa"}}>
            <PortalTopbar/>
            <Container maxWidth="md" sx={{py: 5}}>
                <Stack spacing={3}>
                    <Box>
                        <Typography variant="h4" sx={{fontWeight: 700, color: PRIMARY}}>
                            Adicionar projeto
                        </Typography>
                        <Typography color="text.secondary">
                            Os campos assinalados com * são obrigatórios.
                        </Typography>
                    </Box>

                    <Box sx={{bgcolor: "background.paper", border: 1, borderColor: "divider", borderRadius: 2, p: 4}}>
                        <form onSubmit={submit} noValidate>
                            <Stack spacing={3}>
                                {error && <Alert severity="error">{error}</Alert>}

                                <Stack direction={{xs: "column", sm: "row"}} spacing={2} alignItems="center">
                                    <Avatar
                                        src={imagePreview || undefined}
                                        sx={{width: 80, height: 80}}
                                    >
                                        🏗️
                                    </Avatar>
                                    <Stack spacing={1} sx={{flex: 1}}>
                                        <Typography variant="body2" fontWeight={600}>
                                            Imagem do projeto
                                        </Typography>
                                        <Button
                                            component="label"
                                            variant="outlined"
                                            size="small"
                                            sx={{color: PRIMARY, borderColor: PRIMARY, alignSelf: "flex-start"}}
                                        >
                                            Escolher imagem
                                            <input type="file" accept="image/*" hidden onChange={onImageChange}/>
                                        </Button>
                                        <Typography variant="caption" color="text.secondary">
                                            Opcional — se não escolheres, é usada a imagem padrão.
                                        </Typography>
                                    </Stack>
                                </Stack>

                                <TextField
                                    label="Título do projeto"
                                    size="small"
                                    required
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                />

                                <TextField
                                    label="Associação"
                                    size="small"
                                    value={associationName || "—"}
                                    InputProps={{readOnly: true}}
                                    helperText="O projeto fica associado à vossa associação automaticamente."
                                />

                                <TextField
                                    label="Localização"
                                    size="small"
                                    required
                                    placeholder="Ex.: Lisboa, Porto, Remoto…"
                                    value={location}
                                    onChange={(e) => setLocation(e.target.value)}
                                />

                                <Stack direction={{xs: "column", sm: "row"}} spacing={2}>
                                    <TextField
                                        select
                                        label="Curso (colégio)"
                                        size="small"
                                        required
                                        value={college}
                                        onChange={(e) => setCollege(e.target.value)}
                                        sx={{flex: 1}}
                                        helperText={college ? "" : "Escolhe uma opção"}
                                    >
                                        {COLLEGES.map((c) => (
                                            <MenuItem key={c} value={c}>{c}</MenuItem>
                                        ))}
                                    </TextField>
                                    <TextField
                                        select
                                        label="Tipo"
                                        size="small"
                                        value={modality}
                                        onChange={(e) => setModality(e.target.value)}
                                        sx={{flex: 1}}
                                    >
                                        {MODALITIES.map((m) => (
                                            <MenuItem key={m} value={m}>{m}</MenuItem>
                                        ))}
                                    </TextField>
                                    <TextField
                                        select
                                        label="Horário"
                                        size="small"
                                        value={schedule}
                                        onChange={(e) => setSchedule(e.target.value)}
                                        sx={{flex: 1}}
                                    >
                                        {SCHEDULES.map((s) => (
                                            <MenuItem key={s} value={s}>{s}</MenuItem>
                                        ))}
                                    </TextField>
                                </Stack>

                                <TextField
                                    label="Descrição"
                                    size="small"
                                    required
                                    multiline
                                    minRows={4}
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    helperText="Descreve o desafio, o que esperas dos engenheiros e os requisitos."
                                />

                                <Stack direction="row" spacing={2}>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        size="large"
                                        disabled={loading}
                                        sx={{bgcolor: PRIMARY, fontWeight: 600}}
                                    >
                                        {loading ? "A submeter…" : "Submeter projeto"}
                                    </Button>
                                    <Button component={RouterLink} to="/app/associacao" variant="text">
                                        Cancelar
                                    </Button>
                                </Stack>
                            </Stack>
                        </form>
                    </Box>
                </Stack>
            </Container>
        </Box>
    );
};

export default ProjectAddPage;